import { useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Camera, FileText, ShieldCheck } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

/**
 * Texto LGPD — espelho de apps/api/src/lib/lgpd-texts.ts OCR_CAPTURE_WARNING.
 * Se mudar lá, atualizar aqui (sync manual — sem package shared ainda).
 */
const OCR_CAPTURE_WARNING_PARTS = {
  intro:
    'Você está prestes a capturar a imagem de um DOCUMENTO (nota fiscal, recibo, comprovante, boleto ou extrato bancário). Antes de continuar:',
  bullets: [
    'Enquadre APENAS o documento — evite capturar pessoas, ambientes ou outros papéis fora do enquadramento.',
    'A imagem será mantida PERMANENTEMENTE como prova legal (Lei 8.666/93 + LGPD Art. 7º, II — cumprimento de obrigação legal).',
    'O acesso à imagem original fica restrito ao administrador do workspace e é registrado em log de auditoria.',
    'Dados extraídos (valores, datas, nomes) podem ser corrigidos antes do envio definitivo.',
  ],
  responsibility:
    'A plataforma GestAKI é uma ferramenta de meio: a responsabilidade pelo conteúdo capturado, pelo enquadramento e pela ausência de dados pessoais de terceiros na imagem é INTEGRALMENTE do usuário que efetuar a captura.',
};

interface OcrConsentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConsent: () => void;
}

export function OcrConsentModal({ open, onOpenChange, onConsent }: OcrConsentModalProps) {
  const [accepted, setAccepted] = useState(false);

  function handleConfirm() {
    if (!accepted) return;
    onConsent();
    setAccepted(false);
  }

  function handleCancel() {
    setAccepted(false);
    onOpenChange(false);
  }

  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) setAccepted(false);
        onOpenChange(next);
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            'fixed inset-0 z-40 bg-[rgba(20,16,8,0.40)] backdrop-blur-[2px]',
            'data-[state=open]:animate-fade-in',
          )}
        />
        <DialogPrimitive.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 w-[min(94vw,560px)]',
            'rounded-3 border border-card-stroke bg-card shadow-clay p-6',
            'data-[state=open]:animate-fade-in',
            'max-h-[90vh] overflow-y-auto',
          )}
        >
          <div className="flex items-start gap-3">
            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-accent-soft text-accent">
              <Camera className="h-5 w-5" strokeWidth={1.8} />
            </div>
            <div className="flex-1 min-w-0">
              <DialogPrimitive.Title className="font-display text-[18px] font-bold tracking-[-0.015em] text-fg leading-tight">
                Captura de documento
              </DialogPrimitive.Title>
              <DialogPrimitive.Description className="text-sm text-fg-muted mt-1.5">
                Leia o aviso abaixo antes de abrir a câmera ou selecionar uma imagem.
              </DialogPrimitive.Description>
            </div>
          </div>

          <div className="mt-5 space-y-4 text-sm text-fg">
            <p>{OCR_CAPTURE_WARNING_PARTS.intro}</p>

            <ul className="space-y-2.5">
              {OCR_CAPTURE_WARNING_PARTS.bullets.map((bullet, idx) => (
                <li key={idx} className="flex gap-2.5">
                  <FileText className="h-4 w-4 mt-0.5 shrink-0 text-fg-muted" strokeWidth={1.6} />
                  <span className="text-[13px] leading-relaxed text-fg-muted">{bullet}</span>
                </li>
              ))}
            </ul>

            <div className="flex gap-2.5 rounded-2 border border-warn-stroke bg-warn-soft p-3">
              <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0 text-warn" strokeWidth={1.8} />
              <p className="text-[12.5px] leading-relaxed text-fg">
                {OCR_CAPTURE_WARNING_PARTS.responsibility}
              </p>
            </div>

            <label className="flex items-start gap-2.5 cursor-pointer select-none rounded-2 border border-card-stroke p-3 hover:bg-fg/[0.02] transition-colors">
              <input
                type="checkbox"
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-fg-muted text-accent focus:ring-accent"
                aria-describedby="ocr-consent-text"
              />
              <span id="ocr-consent-text" className="text-[13px] text-fg">
                Confirmo estar ciente da política, que vou enquadrar apenas o documento e que a
                responsabilidade pelo conteúdo capturado é minha.
              </span>
            </label>
          </div>

          <div className="mt-6 flex items-center justify-end gap-2">
            <Button variant="ghost" type="button" onClick={handleCancel}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              type="button"
              onClick={handleConfirm}
              disabled={!accepted}
              aria-describedby={!accepted ? 'ocr-consent-hint' : undefined}
            >
              Abrir captura
            </Button>
            {!accepted && (
              <span id="ocr-consent-hint" className="sr-only">
                Marque a confirmação acima para habilitar a captura.
              </span>
            )}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
